import { EventEmitter } from '@/model/EventEmitter'
import { StationEntity } from '@/model/StationEntity'
import { SelectionEventArgs, SelectionManager } from './shared/SelectionManager'
import { BaseService } from '../BaseService'
import { ServiceLocator } from '../ServiceLocator'

/**
 * Abstract base class for station-like services.
 *
 * Provides common selection management infrastructure for services
 * that handle selectable station entities (water stations, rain gauges, etc.).
 *
 * Extends BaseService to provide dependency injection capabilities.
 *
 * @template T - The entity type (must implement StationEntity)
 */
export abstract class BaseStationService<T extends StationEntity> extends BaseService {
  protected selectionManager = new SelectionManager<T>()

  constructor(serviceLocator: ServiceLocator) {
    super(serviceLocator)
  }

  /**
   * Event emitted when selection state changes.
   * Subclasses should expose this as a public readonly property.
   */
  protected get selectionChanged(): EventEmitter<SelectionEventArgs<T>> {
    return this.selectionManager.selectionChanged
  }

  /**
   * Toggle selection for an entity.
   *
   * Subclasses must implement their own public toggle methods
   * (e.g., toggleStation, toggleRainGauge) and call this method,
   * providing the appropriate key extraction logic.
   *
   * @param key - Unique identifier for the entity
   * @param entity - The entity to toggle
   */
  protected toggleEntity(key: number, entity: T): void {
    this.selectionManager.toggle(key, entity)
  }

  /**
   * Clear all selections.
   *
   * Subclasses must implement their own public clear methods
   * (e.g., clearStations, clearRainGauges) and call this method.
   */
  protected clearAllSelections(): void {
    this.selectionManager.clear()
  }

  /**
   * Check if an entity is selected.
   *
   * @param key - Unique identifier for the entity
   * @returns true if selected, false otherwise
   */
  protected isSelected(key: number): boolean {
    return this.selectionManager.has(key)
  }

  /**
   * Get all currently selected entities.
   *
   * @returns Array of selected entities
   */
  protected getSelectedEntities(): T[] {
    return this.selectionManager.getAll()
  }
}
